import { useMemo } from "react";
import _ from "lodash";

import { BASE_URL } from "config/urls";
import { deleteCall, get, post, useFetch, usePaginatedFetch } from "utils/sdk";
import { formatGetParams } from "utils/urls";

import { IOpenAICustomPromptResponse } from "entities/AISummaries/sdk";
import { IPackageDetailsField } from "entities/Checklist/sdk";
import {
  EAITablesDisplayMode,
  EMemoSectionType,
  EMemoSourcesAndUsesSectionDisplayMode,
} from "entities/MemoTemplate/constants";
import { ISignatureCustomField } from "entities/MemoTemplate/sdk";

import { MemoSignatureStatus } from "./components/Sections/MemoESignatureDrawer/constants";
import { ERiskMoveDirection } from "./components/Sections/RisksAndMitigants/constants";

export enum ProformaPDFFileStatus {
  IN_PROGRESS = "IN_PROGRESS",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export enum MemoPDFStatus {
  IN_PROGRESS = "IN_PROGRESS",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export interface IMemo {
  name: string;
  additional_header_info: string;
  sections: IMemoSection[];
  ai_tables_display_mode: EAITablesDisplayMode;
  include_proforma_pdf: boolean;
}

export interface IProformaPDF {
  id: number;
  status: ProformaPDFFileStatus;
  file?: { signed_url: string; original_file_name: string };
}

export interface IMemoSignatureRole {
  id: number;
  name: string;
}

export interface IMemoSignatureUserData {
  id: number;
  avatar: string | null;
  first_name: string;
  last_name: string;
  email: string;
  company_name: string;
}

export type IMemoSignatureRoleAssignee = (
  | {
      user: IMemoSignatureUserData;
      email: undefined;
    }
  | {
      user: undefined;
      email: string;
    }
) & {
  id: number;
  deal_id?: number;
  memo_id?: number;
};

export interface IMemoSignatureRoleWithAssignees extends IMemoSignatureRole {
  order: number;
  assignees: Array<IMemoSignatureRoleAssignee>;
}

export interface IMemoSignature {
  status: MemoSignatureStatus;
  days_interval_time: number;
  reminders_enabled: boolean;
  signed_at?: string;
  user?: IMemoSignatureUserData;
  email?: string;
}

export interface IMemoSignatureRoleProcessTrackingData
  extends IMemoSignatureRole {
  memo_signature_fields: Array<{
    order: number;
    id: number;
    signatures: Array<IMemoSignature>;
  }>;
}

export interface ISnapshotESingProcessTrackingData {
  id: number;
  file: { id: number; original_file_name: string; signed_url: string };
  signed_file: {
    id: number;
    original_file_name: string;
    signed_url: string;
  } | null;
}

export interface IMemoSignatureRoleWithMemoSignatures {
  roles: Array<IMemoSignatureRoleProcessTrackingData>;
  snapshot: ISnapshotESingProcessTrackingData;
}

export interface IMemoSectionExcelTableFieldValue {
  order: number;
  columns: Array<string>;
}

export interface IMemoOpenAIResponse {
  openai_custom_prompt_response:
    | (IOpenAICustomPromptResponse & {
        prompt_id: number;
        openai_document_assistant_id: number;
      })
    | null;
  response: string;
  updated_at: string | null;
}

export interface AIPromptSection {
  source_documents: Array<ISourceDocument>;
}

export interface IMemoSection {
  section_id: number;
  order: number;
  section_type: EMemoSectionType;
  title: string;
  comment_count: number;
  break_before: boolean;
  limit_image_section_height: boolean;
  table_section_compact_view: boolean;
  sources_and_uses_section_display_mode: EMemoSourcesAndUsesSectionDisplayMode;
  amenities_section_display_image: boolean;
  is_na: boolean;
  memo_section_openai_responses: Array<IMemoOpenAIResponse>;
  checklist_table_id: number;
  checklist_table_updated_at: string;
  checklist_table: Array<Array<{ value: string; is_editable: boolean }>>;
  ai_prompt: AIPromptSection | null;
  is_prompt_response_in_progress: boolean;
  memo_signature_field: {
    role: { id: number; name: string };
    order: number;
    signatures: Array<{ status: MemoSignatureStatus }>;
    custom_fields: Array<ISignatureCustomField>;
  } | null;
  risks: Array<{
    id: number;
    title: string;
    description: string;
    template: {
      title: string;
      description: string;
    } | null;
  }>;
  fields?: IPackageDetailsField[];
}

export interface IMemoSectionComment {
  id: number | string;
  created_by: {
    user_id: number;
    first_name: string;
    last_name: string;
    company_name: string;
    avatar: string | null;
  };
  created_at: string;
  message: string;
  seen_by: Array<{
    first_name: string;
    last_name: string;
    user_id: number;
  }>;
}

interface IShareableMemoLink {
  id: number;
  url: string;
}

export interface IDealTeamCompany {
  name: string;
  locations: Array<{
    street: string;
    city: string;
    state: string;
    zipcode: string;
  }>;
  established_in: string;
  website_url: string;
  description: string;
  executive_summary: Array<{
    summary: string;
    created_at: string;
    updated_at: string;
  }>;
}

export interface IDealSummary {
  summary: string;
  updated_at: string | null;
}

export const useMemoDealDetails = ({
  memoId,
  dealId,
}: {
  memoId: number;
  dealId: number;
}) =>
  useFetch<IMemo>(
    !_.isNaN(dealId) && !_.isNaN(memoId)
      ? `v2/memo/memo/${memoId}/deal/${dealId}/details/`
      : undefined
  );

export const useProformaPDFRetrieve = ({ dealId }: { dealId: number }) =>
  useFetch<IProformaPDF>(`v2/deal/${dealId}/proforma-pdf/retrieve/`);

export const updateMemoDealInfo = ({
  memoId,
  dealId,
  additionalHeaderInfo,
}: {
  memoId: number;
  dealId: number;
  additionalHeaderInfo: string;
}) =>
  post<{ additional_header_info: string }>(
    `${BASE_URL}v2/memo/memo/${memoId}/deal/${dealId}/memo-details/update/`,
    { additional_header_info: additionalHeaderInfo }
  );

export const useShareableMemoLink = ({
  dealId,
  memoId,
  enabled,
}: {
  dealId: number;
  memoId: number;
  enabled: boolean;
}) =>
  useFetch<IShareableMemoLink>(
    enabled
      ? `v2/deal/deal/${dealId}/memo/${memoId}/get-share-token/`
      : undefined
  );

export const createShareableMemoLink = (dealId: number, memoId: number) =>
  post<IShareableMemoLink>(
    `${BASE_URL}v2/deal/deal/${dealId}/memo/${memoId}/create-share-token/`
  );

export const expireShareableMemoLink = (dealId: number, memoId: number) =>
  post<{ response?: string }>(
    `${BASE_URL}v2/deal/deal/${dealId}/expire-share-token/`,
    {
      memo_id: memoId,
    }
  );

export const addMemoSectionComment = ({
  dealId,
  sectionId,
  message,
}: {
  dealId: number;
  sectionId: number;
  message: string;
}): Promise<IMemoSectionComment> =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${sectionId}/memo-section-comment/create/`,
    { message }
  );

export const useSectionCommentsList = ({
  dealId,
  memoSectionId,
}: {
  dealId?: number;
  memoSectionId?: number;
}) =>
  useFetch<IMemoSectionComment[]>(
    dealId && memoSectionId
      ? `v2/memo/deal/${dealId}/memo-section/${memoSectionId}/memo-section-comment/list/`
      : undefined
  );

export const markSectionCommentsListAsSeen = ({
  memoSectionId,
  dealId,
}: {
  memoSectionId?: number;
  dealId?: number;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/memo-section-comment/mark-as-seen/`
  );

export const useDealTeamGroup = ({
  dealId,
  groupName,
}: {
  dealId?: number;
  groupName: string;
}) =>
  useFetch<IDealTeamCompany[]>(
    dealId && groupName
      ? `v2/deal/deal/${dealId}/team-group/?group_name=${groupName}`
      : undefined
  );

export const useDealSummaryByCategory = ({
  dealId,
  category,
}: {
  dealId?: number;
  category: string;
}) =>
  useFetch<IDealSummary>(
    dealId && category
      ? `v2/deal/deal/${dealId}/category/${category}/summary/`
      : undefined
  );

export const updateSummary = ({
  dealId,
  category,
  data,
}: {
  dealId: number;
  category: string;
  data: {
    summary: string;
  };
}) =>
  post(
    `${BASE_URL}v2/deal/deal/${dealId}/category/${category}/summary/update/`,
    data
  );

export const memoSectionNAStatusUpdate = ({
  dealId,
  memoSectionId,
  naStatus,
}: {
  dealId: number;
  memoSectionId: number;
  naStatus: boolean;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/na-status/update/`,
    { is_na: naStatus }
  );

export const memoSectionAISummaryResponseCreateOrUpdate = ({
  dealId,
  memoSectionId,
  openAICustomPromptResponseId,
  response,
}: {
  dealId: number;
  memoSectionId: number;
  openAICustomPromptResponseId: number | null;
  response: string;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/openai-response/create-or-update/`,
    { openai_custom_prompt_response_id: openAICustomPromptResponseId, response }
  );

export const memoSectionAISummaryResponseDelete = ({
  dealId,
  memoSectionId,
}: {
  dealId: number;
  memoSectionId: number;
}) =>
  deleteCall(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/openai-response/delete/`
  );

export interface ISourceDocument {
  id: number;
  name: string;
  files: Array<{
    id: number;
    original_file_name: string;
  }>;
  is_missing: boolean;
  is_na: boolean;
}

export const useSourceDocumentsForMemo = ({
  memoId,
  dealId,
  enabled = true,
}: {
  memoId: number;
  dealId: number;
  enabled?: boolean;
}) =>
  useFetch<Array<ISourceDocument>>(
    enabled
      ? `v2/memo/deal/${dealId}/memo/${memoId}/source-document/list/`
      : undefined
  );

export interface IAIAnswerMemoSection {
  id: number;
  title: string;
  is_na: boolean;
  source_documents_count: number;
  ai_prompt: {
    title: string;
    id: number;
    is_response_in_progress: boolean;
  };
  due_diligence_section: {
    field: Array<{ name: string; id: number; value: string }>;
  };
}

export interface ISourceDocumentDetails {
  id: number;
  name: string;
  is_na: boolean;
  files: Array<{
    id: number;
    original_file_name: string;
    file_name: string;
    file_type: string;
    is_file_value_selected_for_ai_data_extraction: boolean;
    custom_field_value_id: number;
  }>;

  memo_sections: Array<IAIAnswerMemoSection>;
}

export const useSourceDocumentRetrieve = ({
  memoId,
  sourceDocumentId,
  dealId,
  enabled,
}: {
  memoId: number;
  sourceDocumentId: number;
  dealId: number;
  enabled: boolean;
}) =>
  useFetch<ISourceDocumentDetails>(
    enabled
      ? `v2/memo/deal/${dealId}/memo/${memoId}/source-document/${sourceDocumentId}/retrieve/`
      : undefined
  );

export interface MemoSectionAIPromptResponse {
  id: number;
  value: string;
}

export const memoSectionAIProcessTrigger = ({
  memoId,
  dealId,
  memoSectionIds,
}: {
  memoId: number;
  dealId: number;
  memoSectionIds: Array<number>;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo/${memoId}/memo-section-ai-process/trigger/`,
    {
      memo_section_ids: memoSectionIds,
    }
  );

export const sourceDocumentFileSignedUrlRetrieve = ({
  dealId,
  fileId,
  sourceDocumentId,
}: {
  fileId: number;
  dealId: number;
  sourceDocumentId: number;
}): Promise<string> =>
  get(
    `${BASE_URL}v2/memo/deal/${dealId}/source-document/${sourceDocumentId}/file/${fileId}/signed-url/retrieve/`
  );

export interface IMemoRiskTemplate {
  id: number;
  title: string;
}

export const useMemoRiskTemplates = ({
  dealId,
  enabled = true,
}: {
  dealId: number;
  enabled?: boolean;
}) =>
  useFetch<Array<IMemoRiskTemplate>>(
    enabled ? `v2/memo/deal/${dealId}/risk-template/list/` : undefined
  );

export interface IMemoRisk {
  id: number;
  title: string;
  description: string;
  template: {
    title: string;
    description: string;
  } | null;
}

export const riskAdd = ({
  dealId,
  memoSectionId,
  riskTemplateId,
}: {
  dealId: number;
  memoSectionId: number;
  riskTemplateId?: number;
}) =>
  post<IMemoRisk>(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/risk/add/`,
    {
      risk_template_id: riskTemplateId,
    }
  );

export const riskUpdate = ({
  dealId,
  memoSectionId,
  title,
  description,
  riskId,
}: {
  dealId: number;
  memoSectionId: number;
  title: string;
  description: string;
  riskId: number;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/risk/${riskId}/update/`,
    { title, description }
  );

export const riskDelete = ({
  dealId,
  memoSectionId,
  riskId,
}: {
  dealId: number;
  memoSectionId: number;
  riskId: number;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/risk/${riskId}/delete/`
  );

export const riskMove = ({
  dealId,
  memoSectionId,
  riskId,
  direction,
}: {
  dealId: number;
  memoSectionId: number;
  riskId: number;
  direction: ERiskMoveDirection;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/risk/${riskId}/move/`,
    {
      direction,
    }
  );

export const memoPDFCreate = ({
  dealId,
  memoId,
  html,
  isCreatedThroughESignFlow,
}: {
  dealId: number;
  memoId: number;
  html: string;
  isCreatedThroughESignFlow: boolean;
}) =>
  post<{ id: number }>(
    `${BASE_URL}v2/memo/deal/${dealId}/memo/${memoId}/pdf/create/`,
    {
      html,
      is_created_through_e_sign_flow: isCreatedThroughESignFlow,
    }
  );

export const memoPDFDelete = ({
  dealId,
  memoId,
  memoPDFId,
}: {
  dealId: number;
  memoId: number;
  memoPDFId: number;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo/${memoId}/pdf/${memoPDFId}/delete/`
  );

export const memoPDFFileWithSignedUrlAndOriginalFileNameRetrieve = ({
  dealId,
  memoId,
  memoPDFId,
}: {
  dealId: number;
  memoId: number;
  memoPDFId: number;
}) =>
  get<{ original_file_name: string; url: string }>(
    `${BASE_URL}v2/memo/deal/${dealId}/memo/${memoId}/pdf/${memoPDFId}/signed-url-and-original-file-name/retrieve/`
  );

export const useMemoPDFFileWithSignedUrlAndOriginalFileName = ({
  dealId,
  memoId,
  memoPDFId,
  enabled = false,
}: {
  dealId: number;
  memoId: number;
  memoPDFId: number;
  enabled?: boolean;
}) =>
  useFetch<{ original_file_name: string; url: string }>(
    enabled
      ? `v2/memo/deal/${dealId}/memo/${memoId}/pdf/${memoPDFId}/signed-url-and-original-file-name/retrieve/`
      : undefined
  );

export const memoPDFSignedDocumentFileDataWithSignedUrlRetrieve = ({
  dealId,
  memoId,
  memoPDFId,
}: {
  dealId: number;
  memoId: number;
  memoPDFId: number;
}) =>
  get<{ original_file_name: string; url: string }>(
    `${BASE_URL}v2/memo/deal/${dealId}/memo/${memoId}/pdf/${memoPDFId}/signed-document-file-signed-url-and-original-file-name/retrieve/`
  );

export interface IMemoPDFSignatureProcessDocument {
  id: number;
  status: MemoPDFStatus;
  file: { original_file_name: string } | null;
}

export interface IMemoPDFDocument {
  id: number;
  status: MemoPDFStatus;
  file: { id: number; original_file_name: string };
  created_at: string;
  created_by: { first_name: string; last_name: string };
  is_created_through_shareable_link: boolean;
}

export const useMemoPDFList = ({
  dealId,
  memoId,
  queryParams,
}: {
  dealId: number;
  memoId: number;
  enabled?: boolean;
  queryParams?: { [key: string]: string };
}) =>
  usePaginatedFetch<IMemoPDFDocument>({
    url: `v2/memo/deal/${dealId}/memo/${memoId}/pdf/list/${formatGetParams(
      queryParams || {}
    )}`,
    params: {
      limit: 15,
    },
    options: {
      dedupingInterval: 0,
    },
  });

export const useMemoSignatureRoleList = () =>
  useFetch<Array<IMemoSignatureRole>>(`v2/memo/memo-signature-role/list/`);

export const useMemoSignatureRoleWithPrefetchedAssigneesList = ({
  dealId,
  memoId,
}: {
  dealId?: number;
  memoId?: number;
}) => {
  const cacheKey = useMemo(() => new Date().toString(), []);

  const filterParams = _.pickBy(
    { deal_id: dealId, memo_id: memoId, cacheKey },
    (value) => !_.isUndefined(value)
  ) as { [key: string]: number };

  return useFetch<Array<IMemoSignatureRoleWithAssignees>>(
    `v2/memo/memo-signature-role-with-assignees/list/${formatGetParams(
      filterParams
    )}`
  );
};
export const useMemoSignatureRoleWithMemoSignaturesList = ({
  dealId,
  memoId,
  enabled = false,
}: {
  dealId: number;
  memoId: number;
  enabled?: boolean;
}) =>
  useFetch<IMemoSignatureRoleWithMemoSignatures>(
    enabled
      ? `v2/memo/deal/${dealId}/memo/${memoId}/memo-signature-role-with-memo-signatures/list/`
      : undefined
  );

export const useUserAssociatedWithDealList = ({
  dealId,
  enabled = false,
}: {
  dealId: number;
  enabled?: boolean;
}) =>
  useFetch<Array<IMemoSignatureUserData>>(
    enabled
      ? `v2/memo/deal/${dealId}/user-associated-with-deal/list/`
      : undefined
  );

export const useCompanyUserList = ({
  enabled = false,
}: {
  enabled?: boolean;
}) =>
  useFetch<Array<IMemoSignatureUserData>>(
    enabled ? `v2/memo/company-user-list/list/` : undefined
  );

export const memoSignatureRoleAssigneeCreateOrUpdate = ({
  roleId,
  memoSignatureAssigneeId,
  memoId,
  dealId,
  userId,
  email,
}: {
  roleId: number;
  memoSignatureAssigneeId?: number;
  memoId?: number;
  dealId?: number;
  userId?: number;
  email?: string;
}) =>
  post(
    `${BASE_URL}v2/memo/role/${roleId}/memo-signature-role-assignee/create-or-update/`,
    {
      memo_signature_assignee_id: memoSignatureAssigneeId,
      memo_id: memoId,
      deal_id: dealId,
      user_id: userId,
      email: email,
    }
  );

export const memoSignatureEmailNotificationProcessFinalize = ({
  dealId,
  memoId,
  memoPDFId,
}: {
  dealId: number;
  memoId: number;
  memoPDFId: number;
}) =>
  post(
    `${BASE_URL}v2/memo/memo/${memoId}/deal/${dealId}/memo-signature-notification-email-process/finalize/`,
    { memo_pdf_id: memoPDFId }
  );

export const memoSignatureEmailNotificationProcessInitiate = ({
  dealId,
  memoId,
  customTextAppendix,
  intervalTime,
  remindersEnabled,
}: {
  dealId: number;
  memoId: number;
  customTextAppendix: string;
  intervalTime: number;
  remindersEnabled: boolean;
}) =>
  post(
    `${BASE_URL}v2/memo/memo/${memoId}/deal/${dealId}/memo-signature-notification-email-process/initiate/`,
    {
      custom_text_appendix: customTextAppendix,
      days_interval_time: intervalTime,
      reminders_enabled: remindersEnabled,
    }
  );

export const useMemoPDFSnapshotRetrieve = ({
  dealId,
  memoId,
  memoPDFId,
  enabled = false,
}: {
  dealId: number;
  memoId: number;
  memoPDFId?: number;
  enabled?: boolean;
}) =>
  useFetch<IMemoPDFSignatureProcessDocument>(
    enabled
      ? `v2/memo/deal/${dealId}/memo/${memoId}/pdf/${memoPDFId}/retrieve/`
      : undefined
  );

export const memoSignatureEmbeddedLinkCreate = ({
  dealId,
  memoId,
  roleId,
}: {
  dealId: number;
  memoId: number;
  roleId: string;
}) =>
  get<{ link: string | null; status: MemoSignatureStatus }>(
    `${BASE_URL}v2/memo/${memoId}/deal/${dealId}/role/${roleId}/memo-signature-embedded-link/create/`
  );

export const useMemoSignatureDealNameAndMemoName = ({
  dealId,
  memoId,
}: {
  dealId: number;
  memoId: number;
}) =>
  useFetch<{ deal_name: string; memo_name: string }>(
    `v2/memo/${memoId}/deal/${dealId}/memo-signature-deal-name-and-memo-name/retrieve/`
  );

export const memoSignatureStatusUpdate = ({
  dealId,
  memoId,
  roleId,
  action,
}: {
  dealId: number;
  memoId: number;
  roleId: string;
  action: string;
}) =>
  post(
    `${BASE_URL}v2/memo/${memoId}/deal/${dealId}/role/${roleId}/memo-signature-status/update/`,
    { action }
  );

export const memoSignatureProcessCancel = ({
  dealId,
  memoId,
}: {
  dealId: number;
  memoId: number;
}) =>
  post(
    `${BASE_URL}v2/memo/${memoId}/deal/${dealId}/memo-signature-process/cancel/`
  );

export interface IMemoSignatureData extends IMemoSignature {
  id: number;
  memo: { id: number; name: string };
  deal: { id: number; name: string };
  company_name: string;
  role: { id: string; name: string };
  is_completed: boolean;
  sent_at: string;
}

export const useMemoSignatures = () => {
  const cacheKey = useMemo(() => new Date().toString(), []);

  return useFetch<Array<IMemoSignatureData>>(
    `v2/memo/memo-signature/list/${formatGetParams({ cacheKey })}`
  );
};

export const useMemoPDFWithSignedDocumentFile = ({
  dealId,
  memoId,
}: {
  dealId: number;
  memoId: number;
}) =>
  useFetch<{
    id: number;
    deal_name: string;
    memo_name: string;
  }>(
    `v2/memo/${memoId}/deal/${dealId}/memo-pdf-with-signed-document-file/retrieve/`
  );

export const memoSignatureReminderUpdate = ({
  dealId,
  memoId,
  intervalTime,
}: {
  dealId: number;
  memoId: number;
  intervalTime: number;
}) =>
  post(
    `${BASE_URL}v2/memo/${memoId}/deal/${dealId}/memo-signature-reminder/update/`,
    { days_interval_time: intervalTime }
  );

export const memoSignatureReminderToggle = ({
  dealId,
  memoId,
  remindersEnabled,
}: {
  dealId: number;
  memoId: number;
  remindersEnabled: boolean;
}) =>
  post(
    `${BASE_URL}v2/memo/${memoId}/deal/${dealId}/memo-signature-reminder/toggle/`,
    { reminders_enabled: remindersEnabled }
  );

export const memoSignatureReminderTrigger = ({
  dealId,
  memoId,
  roleId,
}: {
  dealId: number;
  memoId: number;
  roleId: number;
}) =>
  post(
    `${BASE_URL}v2/memo/${memoId}/deal/${dealId}/role/${roleId}/memo-signature-reminder/trigger/`
  );
